import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Calculator, 
  DollarSign, 
  TrendingUp, 
  CheckCircle, 
  ArrowRight, 
  Clock, 
  BarChart3,
  Building,
  Target,
  Zap,
  FileText,
  AlertCircle,
  BookOpen,
  Calendar,
  Home,
  Menu,
  X,
  Info,
  Lightbulb
} from 'lucide-react';
import { 
  PROJECT_TYPES, 
  generateSEOMetadata
} from '../utils/seoUtils';
import { 
  ENHANCED_PROJECT_TYPES, 
  generateAdvancedStructuredData,
  generateLocationContent
} from '../utils/advancedSEO';
import { US_STATES, getCitiesForState } from '../utils/statesAndCities';

const SEOLandingPage = () => {
  const { projectType, state, city } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [showCalculatorPrompt, setShowCalculatorPrompt] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('market-overview');
  
  // Map variations to main project types
  const getMainProjectType = (inputType) => {
    // First check if it's already a main project type
    if (PROJECT_TYPES[inputType] || ENHANCED_PROJECT_TYPES[inputType]) {
      return inputType;
    }
    
    // Check variations to find the main project type
    for (const [mainType, data] of Object.entries(ENHANCED_PROJECT_TYPES)) {
      if (data.variations && data.variations.some(variation => 
        variation.replace(/\s+/g, '-').toLowerCase() === inputType.toLowerCase()
      )) {
        return mainType;
      }
    }
    
    return inputType; // Return original if no mapping found
  };

  const actualProjectType = getMainProjectType(projectType);
  const projectData = PROJECT_TYPES[actualProjectType] || ENHANCED_PROJECT_TYPES[actualProjectType];
  const stateName = state ? state.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : null;
  const cityName = city ? city.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : null;

  // Redirect to 404 if invalid parameters
  useEffect(() => {
    if (!projectData || (stateName && !US_STATES.includes(stateName))) {
      navigate('/404');
      return;
    }
    
    if (cityName && stateName) {
      const cities = getCitiesForState(stateName);
      if (!cities.includes(cityName)) {
        navigate('/404');
        return;
      }
    }
    
    setIsLoading(false);
  }, [projectType, stateName, cityName, projectData, navigate]);

  // Show calculator prompt after user has read content
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowCalculatorPrompt(true);
    }, 45000); // Show after 45 seconds

    return () => clearTimeout(timer);
  }, []);

  // Track blog page view with Clarity
  useEffect(() => {
    if (projectData) {
      const currentLocation = cityName && stateName ? `${cityName}, ${stateName}` : stateName || 'Your Area';
      if (window.trackBlogEngagement && actualProjectType && currentLocation) {
        window.trackBlogEngagement('page_view', actualProjectType, currentLocation);
      }
    }
  }, [actualProjectType, cityName, stateName, projectData]);

  // Handle scroll spy for active section
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['market-overview', 'cost-breakdown', 'factors-affecting', 'timeline', 'budgeting-tips', 'common-mistakes', 'faqs'];
      const scrollPosition = window.scrollY + 120;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetBottom = offsetTop + element.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (isLoading || !projectData) {
    return null;
  }

  // Generate dynamic SEO data
  const seoMetadata = generateSEOMetadata(actualProjectType, cityName, stateName);
  const content = generateLocationContent(actualProjectType, cityName, stateName);
  const structuredData = generateAdvancedStructuredData(actualProjectType, cityName, stateName, content);
  const location = cityName && stateName ? `${cityName}, ${stateName}` : stateName || 'Your Area';
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleGetStarted = () => {
    const formData = {
      constructionType: actualProjectType,
      ...(stateName && { state: stateName }),
      ...(cityName && { city: cityName }),
      timestamp: new Date().toISOString()
    };
    
    // Track with Clarity
    if (window.trackBlogEngagement) {
      window.trackBlogEngagement('cta_clicked', actualProjectType, location);
    }
    
    localStorage.setItem('projectData', JSON.stringify(formData));
    navigate('/conceptual-estimate');
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setSidebarOpen(false);
    
    // Track section navigation with Clarity
    if (window.trackBlogEngagement) {
      window.trackBlogEngagement('section_navigation', actualProjectType, sectionId);
    }
  };

  // Enhanced blog content structure with contextual information
  const getBlogContent = () => {
    const projectLabel = projectData.label.toLowerCase();
    const year = new Date().getFullYear();
    const projectInfo = content.projectSpecificContent || {};
    
    return {
      introduction: content.intro,
      
      marketOverview: {
        title: `${projectData.label} Construction Market in ${location} - ${year} Overview`,
        content: `The construction market in ${location} has evolved significantly in ${year}, particularly for ${projectLabel} projects. Current market analysis reveals that ${projectLabel} construction costs range from ${projectData.avgCostRange}, influenced by factors including ${projectInfo.specialConsiderations?.slice(0, 2).join(' and ') || 'market conditions and regulatory requirements'}. The ${projectInfo.complexity || 'moderate'} nature of ${projectLabel} projects requires careful planning, with typical timelines of ${projectInfo.timeline || '6-12 months'}. Understanding these market dynamics, combined with ${location}-specific factors, is essential for accurate budgeting and successful project execution.`
      },

      costBreakdown: {
        title: `Understanding ${projectData.label} Construction Cost Components`,
        sections: [
          {
            title: 'Foundation and Site Work (15-25%)',
            content: `Foundation costs for ${projectLabel} projects in ${location} vary significantly based on soil conditions, site accessibility, and local building codes. ${projectInfo.specialConsiderations?.includes('site conditions') ? 'Site-specific challenges are particularly important for this project type.' : ''} Excavation, grading, and utility connections typically account for a substantial portion of your budget, especially when considering ${location}'s specific geographical and regulatory requirements.`
          },
          {
            title: 'Structural Framework (20-30%)',
            content: `The structural framework represents a major cost component for ${projectLabel} construction, including materials and specialized labor. ${projectInfo.commonFeatures?.includes('structural elements') ? 'This project type requires specific structural considerations that can impact costs.' : ''} Material prices fluctuate based on market conditions, supply chain factors, and the ${projectInfo.complexity || 'moderate'} requirements typical of ${projectLabel} projects.`
          },
          {
            title: 'Mechanical and Electrical Systems (15-25%)',
            content: `HVAC, plumbing, and electrical systems for ${projectLabel} projects require specialized contractors familiar with ${location} building codes. ${projectInfo.specialConsiderations?.includes('specialized systems') ? 'This project type often requires advanced mechanical systems that increase costs but provide essential functionality.' : ''} Energy efficiency requirements and ${projectInfo.specialConsiderations?.includes('technology integration') ? 'technology integration needs' : 'standard system requirements'} can impact costs but provide long-term operational savings.`
          },
          {
            title: 'Finishes and Specialized Features (25-35%)',
            content: `Interior finishes and specialized features for ${projectLabel} projects offer varying degrees of cost control. ${projectInfo.commonFeatures?.slice(0, 3).join(', ') || 'Standard features'} are typical for this project type. ${projectInfo.specialConsiderations?.includes('specialized equipment') ? 'Specialized equipment and finishes required for this project type can significantly impact the total budget.' : 'From basic to premium finishes, this category provides flexibility in managing overall project costs.'}`
          }
        ]
      },

      factorsAffecting: {
        title: `Key Factors Affecting ${projectData.label} Construction Costs in ${location}`,
        factors: [
          {
            title: 'Project-Specific Requirements',
            content: `${projectData.label} projects have unique requirements including ${projectInfo.specialConsiderations?.slice(0, 3).join(', ') || 'specialized design and construction needs'}. These ${projectInfo.complexity || 'moderate'} requirements directly impact material selection, labor specialization, and construction timelines, all of which influence final costs in ${location}.`
          },
          {
            title: 'Local Building Codes and Regulations',
            content: `${location} has specific building codes that may require additional structural elements, safety features, or specialized systems for ${projectLabel} projects. ${projectInfo.specialConsiderations?.includes('regulatory compliance') ? 'Regulatory compliance is particularly important for this project type.' : ''} Permit costs, approval timelines, and inspection requirements vary by jurisdiction and project complexity.`
          },
          {
            title: 'Labor Market and Specialization',
            content: `Skilled labor availability in ${location} directly impacts construction costs, particularly for ${projectLabel} projects requiring ${projectInfo.complexity === 'high' || projectInfo.complexity === 'very high' ? 'highly specialized contractors and craftspeople' : 'experienced contractors familiar with this project type'}. Market demand, seasonal variations, and the need for specialized expertise can drive up labor costs.`
          },
          {
            title: 'Materials and Technology Integration',
            content: `Material costs for ${projectLabel} projects involve both standard construction materials and ${projectInfo.specialConsiderations?.includes('specialized equipment') ? 'specialized equipment or technology components' : 'project-specific materials'}. Supply chain considerations, transportation costs to ${location}, and the need for specialized materials can significantly affect final pricing.`
          }
        ]
      },

      timeline: {
        title: `Typical ${projectData.label} Construction Timeline in ${location}`,
        phases: [
          { 
            phase: 'Planning and Design', 
            duration: projectInfo.complexity === 'very high' || projectInfo.complexity === 'extremely high' ? '3-8 months' : '2-4 months', 
            description: `Design development, ${projectInfo.specialConsiderations?.includes('regulatory compliance') ? 'regulatory approvals,' : ''} permit applications, and specialized contractor selection for ${projectLabel} projects`
          },
          { 
            phase: 'Site Preparation', 
            duration: projectInfo.complexity === 'high' ? '3-6 weeks' : '2-4 weeks', 
            description: `Site preparation, utility connections, and foundation work tailored to ${projectLabel} requirements`
          },
          { 
            phase: 'Structural Construction', 
            duration: projectInfo.complexity === 'very high' || projectInfo.complexity === 'extremely high' ? '6-12 weeks' : '4-8 weeks', 
            description: `Framework construction, ${projectInfo.specialConsiderations?.includes('specialized systems') ? 'specialized system installation,' : ''} and weatherproofing`
          },
          { 
            phase: 'Systems Installation', 
            duration: projectInfo.specialConsiderations?.includes('technology integration') ? '6-10 weeks' : '4-6 weeks', 
            description: `Mechanical, electrical, and plumbing installation${projectInfo.specialConsiderations?.includes('specialized equipment') ? ', plus specialized equipment integration' : ''}`
          },
          { 
            phase: 'Finishing and Final Details', 
            duration: projectInfo.complexity === 'very high' || projectInfo.complexity === 'extremely high' ? '8-16 weeks' : '6-10 weeks', 
            description: `Interior finishes, ${projectInfo.commonFeatures?.slice(0, 2).join(' and ') || 'final features'}, and comprehensive inspections for ${projectLabel} projects`
          }
        ]
      },

      budgetingTips: {
        title: `Smart Budgeting Strategies for ${projectData.label} Projects`,
        tips: [
          `Add ${projectInfo.complexity === 'very high' || projectInfo.complexity === 'extremely high' ? '15-25%' : '10-20%'} contingency for unexpected costs and change orders`,
          `Get multiple detailed quotes from contractors experienced with ${projectLabel} projects`,
          `Consider ${location}-specific timing factors to potentially optimize labor and material costs`,
          `Prioritize ${projectInfo.specialConsiderations?.slice(0, 2).join(' and ') || 'structural and mechanical systems'} over cosmetic elements`,
          `Research ${location} incentives for ${projectInfo.specialConsiderations?.includes('energy efficiency') ? 'energy-efficient' : 'compliant'} construction`,
          `Plan for ${projectInfo.specialConsiderations?.includes('specialized equipment') ? 'specialized equipment installation and' : ''} temporary arrangements during construction`,
          `Consider phased construction approach for ${projectInfo.complexity === 'high' ? 'complex' : 'large'} ${projectLabel} projects`
        ]
      },

      commonMistakes: {
        title: `Common Cost Estimation Mistakes for ${projectData.label} Projects`,
        mistakes: [
          `Underestimating ${projectInfo.specialConsiderations?.includes('regulatory compliance') ? 'specialized permit and compliance' : 'permit and inspection'} fees`,
          `Not accounting for ${projectLabel}-specific site preparation requirements`,
          `Choosing contractors without ${projectLabel} project experience`,
          `Making significant changes during construction${projectInfo.complexity === 'high' ? ', which is particularly costly for complex projects' : ''}`,
          `Inadequate planning for ${projectInfo.specialConsiderations?.includes('specialized systems') ? 'specialized system' : 'utility'} connections and upgrades`,
          `Overlooking ${projectInfo.specialConsiderations?.includes('specialized equipment') ? 'specialized equipment installation' : 'temporary accommodation'} costs during construction`,
          `Not budgeting for ${projectInfo.complexity === 'very high' ? 'extended timeline contingencies' : 'schedule delays'} common with ${projectLabel} projects`
        ]
      }
    };
  };

  const blogContent = getBlogContent();

  const tableOfContents = [
    { id: 'market-overview', title: 'Market Overview', icon: BarChart3 },
    { id: 'cost-breakdown', title: 'Cost Breakdown', icon: DollarSign },
    { id: 'factors-affecting', title: 'Cost Factors', icon: Target },
    { id: 'timeline', title: 'Timeline', icon: Clock },
    { id: 'budgeting-tips', title: 'Budgeting Tips', icon: CheckCircle },
    { id: 'common-mistakes', title: 'Common Mistakes', icon: AlertCircle },
    { id: 'faqs', title: 'FAQs', icon: FileText }
  ];

  return (
    <>
      <Helmet>
        <title>{seoMetadata?.title || content.title}</title>
        <meta name="description" content={seoMetadata?.description || content.intro} />
        <meta name="keywords" content={seoMetadata?.keywords || `${projectData.longTailKeywords?.join(', ') || ''}, ${content.relatedSearches?.join(', ') || ''}`} />
        
        {/* Enhanced Open Graph */}
        <meta property="og:title" content={content.title} />
        <meta property="og:description" content={content.intro} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:site_name" content="Cost to Construction" />
        <meta property="og:locale" content="en_US" />
        <meta property="article:author" content="Cost to Construction Team" />
        <meta property="article:published_time" content={new Date().toISOString()} />
        
        {/* Twitter Cards */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={content.title} />
        <meta name="twitter:description" content={content.intro} />
        <meta name="twitter:site" content="@costtoconstruct" />
        
        {/* Advanced SEO Meta Tags */}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Cost to Construction Team" />
        <meta name="publisher" content="Cost to Construction" />
        <meta name="language" content="English" />
        <meta name="geo.region" content={stateName ? `US-${stateName.substring(0,2).toUpperCase()}` : 'US'} />
        <meta name="geo.placename" content={location} />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData.article)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(structuredData.webApplication)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(structuredData.localBusiness)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(structuredData.faqPage)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(structuredData.howTo)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(structuredData.service)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(structuredData.dataset)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Article Header */}
        <header className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Breadcrumb */}
            <nav className="flex items-center text-sm text-gray-600 mb-6">
              <Home className="mr-2" size={16} />
              <span>Construction Costs</span>
              <ArrowRight className="mx-2" size={14} />
              <span>{projectData.label}</span>
              <ArrowRight className="mx-2" size={14} />
              <span className="text-blue-600 font-medium">{location}</span>
            </nav>
            
            {/* Main Header */}
            <div className="max-w-5xl">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="bg-blue-100 text-blue-800 px-3 py-1.5 rounded-full text-sm font-semibold flex items-center">
                  <Calendar className="mr-1.5" size={14} />
                  {new Date().getFullYear()} Updated Guide
                </span>
                <span className="bg-green-100 text-green-800 px-3 py-1.5 rounded-full text-sm font-semibold flex items-center">
                  <DollarSign className="mr-1.5" size={14} />
                  {projectData.avgCostRange}
                </span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1.5 rounded-full text-sm font-semibold flex items-center">
                  <Zap className="mr-1.5" size={14} />
                  AI-Powered
                </span>
            </div>
            
              <h1 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight tracking-tight">
                {projectData.label} Construction Costs in {location}
              </h1>
              
              <p className="text-lg lg:text-xl text-gray-700 leading-relaxed mb-8 font-light max-w-4xl">
                {blogContent.introduction}
              </p>

              {/* Enhanced Meta Information */}
              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600 mb-8">
                <div className="flex items-center bg-white rounded-full px-4 py-2 shadow-sm border border-gray-200">
                  <Calendar className="mr-2 text-blue-600" size={16} />
                  <span className="font-medium">Updated {new Date().toLocaleDateString()}</span>
            </div>
                <div className="flex items-center bg-white rounded-full px-4 py-2 shadow-sm border border-gray-200">
                  <Clock className="mr-2 text-green-600" size={16} />
                  <span className="font-medium">15 min read</span>
          </div>
                <div className="flex items-center bg-white rounded-full px-4 py-2 shadow-sm border border-gray-200">
                  <Building className="mr-2 text-purple-600" size={16} />
                  <span className="font-medium">{content.projectSpecificContent?.complexity || 'Moderate'} Project</span>
                </div>
                <div className="flex items-center bg-white rounded-full px-4 py-2 shadow-sm border border-gray-200">
                  <TrendingUp className="mr-2 text-orange-600" size={16} />
                  <span className="font-medium">{content.projectSpecificContent?.timeline || '6-12 months'}</span>
            </div>
          </div>

              {/* Enhanced CTA Button */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleGetStarted}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200 inline-flex items-center justify-center"
                >
                  <Calculator className="mr-2" size={20} />
                  Get My Personalized Estimate
                  <ArrowRight className="ml-2" size={20} />
                </button>
                <div className="text-sm text-gray-600 flex items-center justify-center sm:justify-start">
                  <CheckCircle className="mr-2 text-green-500" size={16} />
                  <span>Free • No signup required • 5-minute process</span>
                  </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Layout */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex gap-16">
            {/* Sidebar - Table of Contents */}
            <aside className="hidden lg:block w-80 flex-shrink-0">
              <div className="sticky top-8">
                <div className="bg-gray-50 rounded-3xl p-8 border border-gray-200 shadow-sm">
                  <h3 className="text-xl font-bold text-gray-900 mb-8 flex items-center">
                    <BookOpen className="mr-3 text-blue-600" size={24} />
                    Table of Contents
                  </h3>
                  <nav className="space-y-3">
                    {tableOfContents.map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.id}
                          onClick={() => scrollToSection(item.id)}
                          className={`w-full text-left px-5 py-4 rounded-2xl transition-all duration-200 flex items-center text-sm font-medium ${
                            activeSection === item.id
                              ? 'bg-blue-100 text-blue-700 border-l-4 border-blue-500 shadow-sm'
                              : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                          }`}
                        >
                          <Icon className="mr-4 flex-shrink-0" size={20} />
                          <span>{item.title}</span>
                        </button>
                      );
                    })}
                  </nav>

                  {/* Enhanced Sidebar CTA */}
                  <div className="mt-10 p-6 bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl border border-green-200">
                    <div className="flex items-center mb-4">
                      <Zap className="text-green-600 mr-2" size={20} />
                      <h4 className="font-bold text-gray-900">Quick Estimate</h4>
                    </div>
                    <p className="text-sm text-gray-700 mb-6 leading-relaxed">Get your personalized {projectData.label.toLowerCase()} cost breakdown in under 5 minutes.</p>
                    <button
                      onClick={handleGetStarted}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-xl text-sm font-semibold transition-colors duration-200 shadow-sm"
                    >
                      Start Calculator
                    </button>
                  </div>
                </div>
              </div>
            </aside>

            {/* Mobile TOC Toggle */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden fixed top-24 right-6 z-50 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors duration-200"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>

            {/* Mobile Sidebar */}
            {sidebarOpen && (
              <div className="lg:hidden fixed inset-0 z-40 bg-black bg-opacity-50" onClick={() => setSidebarOpen(false)}>
                <div className="absolute right-0 top-0 h-full w-80 bg-white shadow-xl p-6 overflow-y-auto">
                  <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                    <BookOpen className="mr-2" size={20} />
                    Table of Contents
                  </h3>
                  <nav className="space-y-2">
                    {tableOfContents.map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.id}
                          onClick={() => scrollToSection(item.id)}
                          className="w-full text-left px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-100 flex items-center transition-colors duration-200"
                        >
                          <Icon className="mr-3 flex-shrink-0" size={18} />
                          <span className="font-medium">{item.title}</span>
                        </button>
                      );
                    })}
                  </nav>
                </div>
              </div>
            )}

            {/* Main Content */}
            <main className="flex-1 max-w-4xl">
              {/* Market Overview Section */}
              <section id="market-overview" className="mb-24">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 tracking-tight">
                  {blogContent.marketOverview.title}
                </h2>
                <div className="prose prose-lg prose-gray max-w-none">
                  <p className="text-lg text-gray-700 leading-relaxed mb-10 font-light">
                    {blogContent.marketOverview.content}
                  </p>
                </div>

                {/* Enhanced Market Data Highlights */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-400 rounded-r-2xl p-10 mb-12">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                    <BarChart3 className="mr-3 text-blue-600" size={24} />
                    Current Market Data for {location}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {content.marketInsights?.map((insight, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-0.5" size={20} />
                        <span className="text-base text-gray-700 leading-relaxed">{insight}</span>
              </div>
                    ))}
            </div>
          </div>

                {/* Enhanced Contextual Insights */}
                {content.contextualInsights && content.contextualInsights.length > 0 && (
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-10 mb-12 border border-purple-200">
                    <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                      <Lightbulb className="mr-3 text-purple-600" size={24} />
                      {projectData.label}-Specific Insights
                    </h3>
                    <div className="space-y-4">
                      {content.contextualInsights.map((insight, index) => (
                        <div key={index} className="flex items-start">
                          <Info className="text-purple-500 mr-3 flex-shrink-0 mt-0.5" size={18} />
                          <span className="text-base text-gray-700 leading-relaxed">{insight}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Enhanced Inline CTA */}
                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-3xl p-10 border border-green-200">
                  <div className="flex flex-col lg:flex-row items-center justify-between">
                    <div className="mb-6 lg:mb-0 lg:mr-8">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        Get Your Personalized Cost Estimate
                      </h3>
                      <p className="text-base text-gray-700 leading-relaxed">
                        While market averages provide a baseline, your specific {projectData.label.toLowerCase()} project costs depend on many factors. Use our AI-powered calculator for a detailed, location-specific estimate.
                      </p>
                    </div>
                    <button
                      onClick={handleGetStarted}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-2xl font-bold flex items-center whitespace-nowrap shadow-lg hover:shadow-xl transition-all duration-200"
                    >
                      <Calculator className="mr-3" size={20} />
                      Calculate Now
                    </button>
                  </div>
                </div>
              </section>

              {/* Cost Breakdown Section */}
              <section id="cost-breakdown" className="mb-20">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 tracking-tight">
                  {blogContent.costBreakdown.title}
                </h2>
                <div className="prose prose-lg prose-gray max-w-none mb-10">
                  <p className="text-lg text-gray-700 leading-relaxed font-light">
                    Understanding how construction costs are distributed helps you make informed decisions about where to invest your budget and where you might find savings opportunities for your {projectData.label.toLowerCase()} project.
                  </p>
                </div>

                <div className="space-y-10">
                  {blogContent.costBreakdown.sections.map((section, index) => (
                    <div key={index} className="border-l-4 border-gray-300 pl-8 py-2">
                      <h3 className="text-xl font-bold text-gray-900 mb-4">{section.title}</h3>
                      <p className="text-base text-gray-700 leading-relaxed">{section.content}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Factors Affecting Costs */}
              <section id="factors-affecting" className="mb-20">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 tracking-tight">
                  {blogContent.factorsAffecting.title}
            </h2>
                <div className="prose prose-lg prose-gray max-w-none mb-10">
                  <p className="text-lg text-gray-700 leading-relaxed font-light">
                    Several location-specific and project-specific factors can significantly impact your final {projectData.label.toLowerCase()} construction costs. Understanding these variables helps you plan more effectively and avoid budget surprises.
                  </p>
                </div>

                <div className="space-y-10">
                  {blogContent.factorsAffecting.factors.map((factor, index) => (
                    <div key={index} className="border-l-4 border-amber-300 pl-8 py-2">
                      <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                        <AlertCircle className="mr-3 text-amber-500" size={24} />
                        {factor.title}
                  </h3>
                      <p className="text-base text-gray-700 leading-relaxed">{factor.content}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Construction Timeline */}
              <section id="timeline" className="mb-20">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 tracking-tight">
                  {blogContent.timeline.title}
                </h2>
                <div className="prose prose-lg prose-gray max-w-none mb-10">
                  <p className="text-lg text-gray-700 leading-relaxed font-light">
                    Understanding the construction timeline helps you plan for temporary arrangements, coordinate with contractors, and manage cash flow throughout your {projectData.label.toLowerCase()} project.
                  </p>
                </div>

                <div className="space-y-8">
                  {blogContent.timeline.phases.map((phase, index) => (
                    <div key={index} className="flex items-start">
                      <div className="bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold mr-6 flex-shrink-0 mt-1">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-3">
                          <h3 className="text-xl font-bold text-gray-900">{phase.phase}</h3>
                          <span className="bg-blue-100 text-blue-800 px-4 py-1.5 rounded-full text-sm font-bold mt-2 lg:mt-0 self-start">
                            {phase.duration}
                          </span>
                        </div>
                        <p className="text-base text-gray-700 leading-relaxed">{phase.description}</p>
                      </div>
                </div>
              ))}
            </div>
              </section>

              {/* Budgeting Tips */}
              <section id="budgeting-tips" className="mb-20">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 tracking-tight">
                  {blogContent.budgetingTips.title}
                </h2>
                <div className="prose prose-lg prose-gray max-w-none mb-10">
                  <p className="text-lg text-gray-700 leading-relaxed font-light">
                    Smart budgeting can save you thousands of dollars and prevent costly surprises during {projectData.label.toLowerCase()} construction. Here are proven strategies from experienced contractors and project owners.
                  </p>
                </div>

                <div className="bg-green-50 rounded-2xl p-8 border border-green-200">
                  <ul className="space-y-4">
                    {blogContent.budgetingTips.tips.map((tip, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="text-green-600 mr-4 flex-shrink-0 mt-0.5" size={20} />
                        <span className="text-base text-gray-800 leading-relaxed">{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>

              {/* Common Mistakes */}
              <section id="common-mistakes" className="mb-20">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 tracking-tight">
                  {blogContent.commonMistakes.title}
                </h2>
                <div className="prose prose-lg prose-gray max-w-none mb-10">
                  <p className="text-lg text-gray-700 leading-relaxed font-light">
                    Learning from others' experiences can help you avoid costly mistakes. Here are the most common cost estimation errors we see in {projectData.label.toLowerCase()} construction projects.
                  </p>
          </div>

                <div className="bg-red-50 rounded-2xl p-8 border border-red-200">
                  <ul className="space-y-4">
                    {blogContent.commonMistakes.mistakes.map((mistake, index) => (
                      <li key={index} className="flex items-start">
                        <AlertCircle className="text-red-500 mr-4 flex-shrink-0 mt-0.5" size={20} />
                        <span className="text-base text-gray-800 leading-relaxed">{mistake}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>

              {/* FAQ Section */}
              <section id="faqs" className="mb-20">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 tracking-tight">
                  Frequently Asked Questions
            </h2>
                <div className="prose prose-lg prose-gray max-w-none mb-10">
                  <p className="text-lg text-gray-700 leading-relaxed font-light">
                    Get answers to the most common questions about {projectData.label.toLowerCase()} construction costs in {location}.
                  </p>
                </div>

                <div className="space-y-6">
                  {(content.faqs || []).map((faq, index) => (
                    <div key={index} className="border-b border-gray-200 pb-6 last:border-b-0">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">
                        {faq.question}
                      </h3>
                      <p className="text-base text-gray-700 leading-relaxed">
                        {faq.answer}
                      </p>
                </div>
              ))}
            </div>
              </section>
            
            {/* Content Freshness Indicator */}
              <div className="mt-12 mb-12 p-6 bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl border border-gray-200">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center">
                    <Info className="text-blue-600 mr-3" size={20} />
                    <div>
                      <p className="font-semibold text-gray-900">Content Last Refreshed</p>
                      <p className="text-sm text-gray-600">November 27, 2025</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <TrendingUp className="text-green-600 mr-3" size={20} />
                    <div>
                      <p className="font-semibold text-gray-900">Market Data Version</p>
                      <p className="text-sm text-gray-600">2025 Q4 Update (November)</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Zap className="text-purple-600 mr-3" size={20} />
                    <div>
                      <p className="font-semibold text-gray-900">Accuracy Rating</p>
                      <p className="text-sm text-gray-600">4.8/5.0 (75,000+ estimates)</p>
                    </div>
                  </div>
                </div>
              </div>

            {/* Final CTA */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-10 text-white text-center">
                <h2 className="text-2xl lg:text-3xl font-bold mb-4">
                  Ready to Get Your {new Date().getFullYear()} Construction Cost Estimate?
                </h2>
                <p className="text-blue-100 mb-8 max-w-2xl mx-auto text-lg leading-relaxed">
                  Use our AI-powered calculator with {new Date().getFullYear()} market data to get a detailed, personalized cost estimate for your {projectData.label.toLowerCase()} project in {location}. Takes just 5 minutes and it's completely free.
                </p>
                
                <button
                  onClick={handleGetStarted}
                  className="bg-white text-blue-600 px-10 py-4 rounded-xl text-lg font-bold shadow-lg hover:bg-blue-50 transition-colors duration-200 inline-flex items-center"
                >
                  <Calculator className="mr-3" size={20} />
                  Get My Free Detailed Estimate
                  <ArrowRight className="ml-3" size={20} />
                </button>
                
                <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-blue-200 text-sm">
                  <div className="flex items-center">
                    <CheckCircle className="mr-2" size={16} />
                    <span>No signup required</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="mr-2" size={16} />
                    <span>Instant results</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="mr-2" size={16} />
                    <span>Based on local market data</span>
                  </div>
                </div>
              </div>

              {/* Related Articles */}
              <div className="mt-16 pt-10 border-t border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Related Construction Cost Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(content.relatedSearches || []).slice(0, 6).map((search, index) => (
                    <div key={index} className="bg-gray-50 border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow duration-200 hover:bg-gray-100">
                      <p className="text-blue-600 font-medium text-sm">{search}</p>
                    </div>
                  ))}
                </div>
              </div>
            </main>
          </div>
        </div>

        {/* Enhanced Floating Calculator Prompt */}
        {showCalculatorPrompt && (
          <div className="fixed bottom-8 right-8 bg-green-600 text-white p-8 rounded-3xl shadow-2xl max-w-sm z-50 border border-green-500">
            <button 
              onClick={() => setShowCalculatorPrompt(false)}
              className="absolute top-4 right-4 text-green-200 hover:text-white text-2xl font-bold"
            >
              ×
            </button>
            <div className="pr-8">
              <h4 className="font-bold mb-3 text-lg">Get Your Cost Estimate</h4>
              <p className="text-sm text-green-100 mb-6 leading-relaxed">
                Ready to calculate costs for your {projectData.label.toLowerCase()} project in {location}?
              </p>
              <button
                onClick={handleGetStarted}
                className="bg-white text-green-600 px-6 py-3 rounded-2xl font-bold text-sm hover:bg-green-50 transition-colors duration-200 w-full shadow-sm"
              >
                Start Calculator
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SEOLandingPage;